import { Injectable, inject } from '@angular/core';
import * as marked from 'marked';
import { Marked } from 'marked';
import DOMPurify from 'isomorphic-dompurify';
import { Subject } from 'rxjs';
import { FileUnifiedService } from '../file-unified/file-unified.service';
import { ExecutionContext } from '../../interfaces/chunk/chunk-context.interface';
import { ResponseType } from '../../constants/general.constants';

@Injectable({
  providedIn: 'root',
})
export class MarkdownService {
  public processedDataSubject$ = new Subject<any>();
  private fileUnifiedService = inject(FileUnifiedService);
  private loadedImages: Map<string, string> = new Map();

  /** Default GFM parse without project image loading; isolated from {@link getMarkdown} renderer state. */
  private readonly simpleMarkdown = new Marked();

  /**
   * Convert markdown to HTML for inline display (LLM text, previews). Does not resolve workflow file images.
   * On parse failure or unexpected async output, returns HTML-escaped source so callers using innerHTML stay safe.
   */
  parseSimpleMarkdownToHtml(src: string): string {
    if (!src) {
      return '';
    }
    try {
      const out = this.simpleMarkdown.parse(src);
      if (typeof out === 'string') {
        return out;
      }
      if (out instanceof Promise) {
        console.warn('MarkdownService.parseSimpleMarkdownToHtml: async parse not supported; using escaped plain text');
        return this.escapeHtml(src);
      }
      return '';
    } catch {
      return this.escapeHtml(src);
    }
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Convert a workflow/cache markdown file's contents to HTML that is safe to
   * inject via innerHTML (SCRUM-712 — `ehq.displayMarkdown`). Two hard rules:
   *
   * 1. No JavaScript — scripts and event handlers are removed by DOMPurify.
   * 2. No remote references — every remote URL (http(s):, other schemes, and
   *    protocol-relative `//host`) is stripped by {@link sanitizeMarkdownHtml}'s
   *    ALLOWED_URI_REGEXP, so nothing is fetched off-device when the tab renders.
   *
   * Local image references in the markdown are resolved to inline `data:` URLs
   * before parsing; references we can't resolve locally render without a `src`
   * (never a remote fetch). The DOMPurify pass also defends against raw remote
   * HTML embedded directly in the markdown source.
   */
  async markdownToSafeHtml(
    src: string,
    stepId: string,
    source?: string,
  ): Promise<string> {
    if (!src) {
      return '';
    }

    const context = this.createContext(stepId);
    const images = await this.resolveLocalImages(src, context, source);

    const renderer = new marked.Renderer();
    // marked 13+ passes renderer methods a single token object instead of
    // positional arguments. (EHQ-171)
    renderer.image = ({ href, title, text }) => {
      const { basePath, dimensions } = this.splitImageSize(href);
      const dataUrl = images.get(basePath);

      // Unresolved (or remote) image references render with no src so the tab
      // never triggers an off-device fetch.
      if (!dataUrl) {
        return `<img alt="${this.escapeHtml(text ?? '')}"${title ? ` title="${this.escapeHtml(title)}"` : ''}>`;
      }

      // Size via width/height attributes — the `style` attribute is forbidden by
      // the sanitizer to block CSS url() based remote loads.
      let dims = '';
      if (dimensions) {
        const [width, height] = dimensions.split('x');
        if (width) dims += ` width="${parseInt(width, 10) || ''}"`;
        if (height) dims += ` height="${parseInt(height, 10) || ''}"`;
      }
      return `<img src="${dataUrl}" alt="${this.escapeHtml(text ?? '')}"${title ? ` title="${this.escapeHtml(title)}"` : ''}${dims}>`;
    };

    let html: string;
    try {
      const out = new Marked({ renderer }).parse(src);
      html = typeof out === 'string' ? out : this.escapeHtml(src);
    } catch {
      html = this.escapeHtml(src);
    }

    return this.sanitizeMarkdownHtml(html);
  }

  /**
   * Find every local image reference in the markdown and load it into a
   * basePath → `data:` URL map. Remote references are skipped (only local
   * workflow/cache files are resolved); failures are swallowed so one bad image
   * never breaks the whole render.
   */
  private async resolveLocalImages(
    data: string,
    context: ExecutionContext | any,
    source?: string,
  ): Promise<Map<string, string>> {
    const images = new Map<string, string>();
    // Negated character classes (not lazy `.*?`) keep this linear / non-
    // backtracking. Group 1 is the full parenthesized target including any
    // ` =WxH` size suffix; splitImageSize() trims that off.
    const matches = [...data.matchAll(/!\[[^\]]*\]\(([^)]*)\)/g)];
    const fileNames = [
      ...new Set(matches.map((m) => this.splitImageSize(m[1]).basePath)),
    ].filter(
      (name) =>
        !!name && !/^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(name), // local refs only
    );

    for (const fileName of fileNames) {
      try {
        const fileContent = await this.fileUnifiedService.getFile(
          [fileName, ResponseType.ArrayBuffer],
          context,
          source,
        );
        if (fileContent instanceof Uint8Array) {
          const blob = new Blob([fileContent as BlobPart], {
            type: this.getMimeType(fileName),
          });
          images.set(fileName, await this.blobToDataUrl(blob));
        }
      } catch (error) {
        console.error(`Failed to load markdown image: ${fileName}`, error);
      }
    }
    return images;
  }

  /**
   * Split an image href into its path and optional `=WxH` size suffix without a
   * backtracking-prone regex. Splitting on the first `=` with `indexOf` is O(n);
   * the previous whitespace-padded regex split was super-linear on long
   * whitespace-only input.
   */
  private splitImageSize(href: string): {
    basePath: string;
    dimensions: string;
  } {
    const eq = href.indexOf('=');
    if (eq === -1) {
      return { basePath: href.trim(), dimensions: '' };
    }
    return {
      basePath: href.slice(0, eq).trim(),
      dimensions: href.slice(eq + 1).trim(),
    };
  }

  /**
   * Sanitize parsed markdown HTML in two passes:
   *
   * 1. DOMPurify strips scripts/handlers (no JS) and remote URLs. The
   *    ALLOWED_URI_REGEXP uses DOMPurify's own disjoint-class idiom (no
   *    backtracking): it permits `data:`/`mailto:`/`tel:` and scheme-less
   *    relative/anchor URLs, and rejects http(s):, other schemes, and
   *    protocol-relative `//host`. Passed as per-call config so global DOMPurify
   *    state (shared with katex/chunk-context) is never mutated.
   * 2. {@link stripNonDataLoads} then removes every resource-loading attribute
   *    whose value is not a `data:` URL, so rendering never triggers a network
   *    fetch — including a *same-origin* one from a relative `src` in raw HTML
   *    embedded in the markdown (which pass 1 alone would keep).
   */
  private sanitizeMarkdownHtml(html: string): string {
    const clean = DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: [
        'script',
        'style',
        'iframe',
        'object',
        'embed',
        'form',
        'input',
        'button',
        'link',
        'base',
        'meta',
      ],
      FORBID_ATTR: ['style', 'srcset', 'ping', 'formaction', 'background'],
      ALLOWED_URI_REGEXP:
        /^(?:(?:data|mailto|tel):|[a-z+.-]+(?:[^a-z+.:-]|$)|\/(?!\/)|[^a-z/])/i,
      ADD_ATTR: ['target'],
    }) as unknown as string;

    return this.stripNonDataLoads(clean);
  }

  /**
   * Defense in depth for the "no remote references" guarantee: after DOMPurify,
   * remove any resource-loading attribute whose value is not a `data:` URL so
   * the rendered markdown never issues a network request on display (not even a
   * same-origin fetch from a relative `src`). `href` is kept only on `<a>` —
   * navigation is a user action, not an automatic load.
   */
  private stripNonDataLoads(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const LOAD_ATTRS = ['src', 'poster', 'xlink:href'];
    doc.body.querySelectorAll('*').forEach((el) => {
      for (const attr of LOAD_ATTRS) {
        const val = el.getAttribute(attr);
        if (val && !/^data:/i.test(val.trim())) {
          el.removeAttribute(attr);
        }
      }
      if (el.tagName !== 'A' && el.hasAttribute('href')) {
        el.removeAttribute('href');
      }
    });
    return doc.body.innerHTML;
  }

  private createContext(stepId: string): ExecutionContext | any {
    return {
      stepId,
      setBusy: () => { },
      addMessage: () => { },
    };
  }

  async getMarkdown(
    data: string,
    stepId: string,
    ignoreCache: boolean = false,
    chunkValueName: 'content' | 'description' = 'content'
  ) {
    try {
      // Reset the loaded images cache
      this.loadedImages.clear();

      // First, find and load all images
      const imageMatches = [
        ...data.matchAll(/!\[.*?\]\((.*?)(?:\s*=\s*\d*x?\d*)?\)/g),
      ];
      const imageFiles = [
        ...new Set(imageMatches.map((match) => match[1].trim())),
      ];

      // Load all images first
      const context = this.createContext(stepId);
      for (const fileName of imageFiles) {
        try {
          const fileContent = await this.fileUnifiedService.getFile(
            [fileName, ResponseType.ArrayBuffer],
            context
          );
          if (fileContent instanceof Uint8Array) {
            const mimeType = this.getMimeType(fileName);
            const blob = new Blob([fileContent as BlobPart], { type: mimeType });
            const dataUrl = await this.blobToDataUrl(blob);
            this.loadedImages.set(fileName, dataUrl);
          }
        } catch (error) {
          console.error(`Failed to load image: ${fileName}`, error);
        }
      }

      // Configure marked with custom renderer
      const renderer = new marked.Renderer();

      // Override image rendering to use our loaded images
      // marked 13+ passes a single token object, not positional args. (EHQ-171)
      renderer.image = ({ href, title, text }) => {
        // Extract size specifications if present
        const [basePath, dimensions] = href.split(/\s*=\s*/);
        const dataUrl = this.loadedImages.get(basePath);

        if (!dataUrl) {
          console.warn(`No data URL found for image: ${basePath}`);
          return `<img src="" alt="${text}"${title ? ` title="${title}"` : ''
            }>`;
        }

        let style = '';
        if (dimensions) {
          const [width, height] = dimensions.split('x');
          style = ' style="';
          if (width) style += `width:${width}px;`;
          if (height) style += `height:${height}px;`;
          style += '"';
        }

        return `<img src="${dataUrl}" alt="${text}"${title ? ` title="${title}"` : ''
          }${style}>`;
      };

      // Set the custom renderer
      marked.setOptions({ renderer });

      // Parse markdown with loaded images
      const markdown = marked.parse(data);

      this.processedDataSubject$.next({
        stepId,
        markdown,
        ignoreCache,
        chunkValueName,
      });
    } catch (error) {
      console.error('Error processing markdown:', error);
      // Fall back to basic markdown parsing
      const markdown = marked.parse(data);

      this.processedDataSubject$.next({
        stepId,
        markdown,
        ignoreCache,
        chunkValueName,
      });
    }
  }

  private getMimeType(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    const mimeTypes: { [key: string]: string } = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      svg: 'image/svg+xml',
      webp: 'image/webp',
      bmp: 'image/bmp',
    };
    return mimeTypes[ext || ''] || 'application/octet-stream';
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert blob to data URL'));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }
}
